import React, { useState, useCallback, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useSnackbar } from 'components/common/SnackbarNotification';
import {
  AddonRequestFormModal,
  AddonRequestViewModal,
} from 'components/modals/modules/addon-request-module/AddonRequestModal';
import type {
  AddonRequestFormData,
  AddonRequestRecord,
  GLEntryFormData,
} from 'components/modals/modules/addon-request-module/AddonRequestModal';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useAddonRequests, useCreateAddonRequest, useUpdateAddonRequest, useDeleteAddonRequest, useSubmitAddonRequest, useGoldRates, useEnumsByGroup } from 'hooks/api';
import { addonRequestService } from 'api/services/addonRequestService';
import { GridLoadingSkeleton } from 'components/common';
// ============================================================================
// INITIAL FORM DATA
// ============================================================================

const createEmptyGL = (): GLEntryFormData => ({
  id: `GL_${Date.now()}`,
  glCustomerId: '',
  glCustomerName: '',
  glNumber: '',
  netWeight: '',
  originalLoanAmount: '',
  outstandingAmount: '',
  documentationCharge: '',
  loanDate: new Date().toISOString().split('T')[0],
});

const emptyFormData: AddonRequestFormData = {
  loanType: '',
  loanAccountNumber: '',
  addonCustomerId: '',
  addonCustomerName: '',
  requestedAmount: '',
  glEntries: [createEmptyGL()],
};

// ============================================================================
// MAIN ADD-ON REQUEST SCREEN COMPONENT
// ============================================================================

const AddonRequestScreen: React.FC = () => {
  // API hooks
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useAddonRequests();
  const { createRequest } = useCreateAddonRequest();
  const { updateRequest } = useUpdateAddonRequest();
  const { deleteRequest } = useDeleteAddonRequest();
  const { submitRequest } = useSubmitAddonRequest();
  const { data: goldRates } = useGoldRates();
  const { data: loanTypeEnums } = useEnumsByGroup('LOAN_TYPE');
  // Data from API
  const requests = (apiData as unknown as AddonRequestRecord[]) || [];
  const activeGoldRateId = useMemo(() => {
    const active = goldRates.find(r => r.isActive);
    return active ? String(active.id) : '';
  }, [goldRates]);
  const loanTypeLookup = useMemo(() => {
    const map: Record<string, string> = {};
    loanTypeEnums.forEach(e => { map[e.enumKey] = e.enumValue; });
    return map;
  }, [loanTypeEnums]);

  // UI state
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<AddonRequestRecord[]>([]);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<AddonRequestRecord | null>(null);
  const [formData, setFormData] = useState<AddonRequestFormData>(emptyFormData);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', variant: 'default', onConfirm: () => {} });

  const { showMessage } = useSnackbar();

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() =>
    requests.filter((req) => {
      const matchesSearch = !searchQuery ||
        (req.customerName ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (req.addonLoanType ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (req.addonLoanAccountNumber ?? '').toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === '' || req.statusCode === statusFilter;

      const matchesDateFrom = !dateFrom || req.createdAt >= dateFrom;
      const matchesDateTo = !dateTo || req.createdAt.split('T')[0] <= dateTo;

      return matchesSearch && matchesStatus && matchesDateFrom && matchesDateTo;
    }),
    [requests, searchQuery, statusFilter, dateFrom, dateTo]
  );

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleAddNew = useCallback(() => {
    setFormMode('create');
    setSelectedRecord(null);
    setFormData({ ...emptyFormData, glEntries: [createEmptyGL()] });
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback(async (record: AddonRequestRecord) => {
    try {
      const response = await addonRequestService.getById(record.id);
      const detail = response.data as unknown as AddonRequestRecord;
      setFormMode('edit');
      setSelectedRecord(detail);
      setFormData({
        loanType: detail.addonLoanType,
        loanAccountNumber: detail.addonLoanAccountNumber,
        addonCustomerId: detail.addonCustomerId,
        addonCustomerName: detail.customerName,
        requestedAmount: String(detail.requestedAmount),
        glEntries: (detail.glEntries ?? []).map((gl) => ({
          id: gl.id,
          glCustomerId: gl.customerId,
          glCustomerName: gl.customerName ?? '',
          glNumber: gl.glNumber,
          netWeight: String(gl.netWeight),
          originalLoanAmount: String(gl.originalLoanAmount),
          outstandingAmount: gl.outstandingAmount != null ? String(gl.outstandingAmount) : '',
          documentationCharge: gl.documentationCharge != null ? String(gl.documentationCharge) : '',
          loanDate: gl.loanDate ? gl.loanDate.split('T')[0] : '',
        })),
      });
      setFormModalOpen(true);
    } catch {
      showMessage('Failed to load request details', 'error');
    }
  }, [showMessage]);

  const handleView = useCallback(async (record: AddonRequestRecord) => {
    try {
      const response = await addonRequestService.getById(record.id);
      const detail = response.data as unknown as AddonRequestRecord;
      setSelectedRecord(detail);
      setViewModalOpen(true);
    } catch {
      showMessage('Failed to load request details', 'error');
    }
  }, [showMessage]);

  const handleSaveForm = useCallback(async () => {
    // Validation
    if (!formData.loanType) { showMessage('Loan Type is required', 'error'); return; }
    if (!formData.loanAccountNumber.trim()) { showMessage('Loan Account Number is required', 'error'); return; }
    if (!formData.addonCustomerId) { showMessage('Customer ID is required', 'error'); return; }

    // Duplicate loan account number validation (checks both addon requests and loan mappings via API)
    try {
      const isDuplicate = await addonRequestService.checkDuplicateLoanAccount(
        formData.loanAccountNumber.trim(),
        selectedRecord?.id
      );
      if (isDuplicate) {
        showMessage(`Loan Account Number "${formData.loanAccountNumber}" already exists`, 'error');
        return;
      }
    } catch {
      showMessage('Failed to validate loan account number', 'error');
      return;
    }

    const hasEmptyGL = formData.glEntries.some((gl) =>
      !gl.glCustomerId || !gl.glNumber.trim() || !gl.netWeight || !gl.originalLoanAmount || !gl.loanDate
    );
    if (hasEmptyGL) { showMessage('All Gold Loan fields are required', 'error'); return; }

    const glNumbers = formData.glEntries.map((gl) => gl.glNumber.trim().toLowerCase());
    const duplicateGlNumbers = glNumbers.filter((n, i) => glNumbers.indexOf(n) !== i);
    if (duplicateGlNumbers.length > 0) {
      showMessage(`Duplicate GL numbers found: ${[...new Set(duplicateGlNumbers)].join(', ')}. Each GL number must be unique.`, 'error');
      return;
    }

    if (!formData.requestedAmount || parseFloat(formData.requestedAmount) <= 0) {
      showMessage('Amount Required must be greater than 0', 'error'); return;
    }

    const action = formMode === 'create' ? 'Create' : 'Save';

    setConfirmDialog({
      open: true,
      title: `${action} Add-On Request`,
      message: formMode === 'create'
        ? 'Are you sure you want to create this add-on request as Draft?'
        : 'Are you sure you want to save changes to this request?',
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          const glPayload = formData.glEntries.map((gl) => ({
            customerId: gl.glCustomerId,
            glNumber: gl.glNumber,
            netWeight: parseFloat(gl.netWeight) || 0,
            originalLoanAmount: parseFloat(gl.originalLoanAmount) || 0,
            outstandingAmount: gl.outstandingAmount ? parseFloat(gl.outstandingAmount) : null,
            documentationCharge: parseFloat(gl.documentationCharge) || 0,
            loanDate: gl.loanDate,
          }));

          if (formMode === 'create') {
              await createRequest({
                addonCustomerId: formData.addonCustomerId,
                addonLoanType: formData.loanType,
                addonLoanAccountNumber: formData.loanAccountNumber,
                requestedAmount: parseFloat(formData.requestedAmount) || 0,
                goldRateId: activeGoldRateId,
                glEntries: glPayload,
              });
            } else if (selectedRecord) {
              await updateRequest(selectedRecord.id, {
                id: selectedRecord.id,
                addonCustomerId: formData.addonCustomerId,
                addonLoanType: formData.loanType,
                addonLoanAccountNumber: formData.loanAccountNumber,
                requestedAmount: parseFloat(formData.requestedAmount) || 0,
                goldRateId: activeGoldRateId,
                glEntries: glPayload,
                rowVersion: selectedRecord.rowVersion,
              });
            }
            await refetch();

          showMessage(`Add-on request ${formMode === 'create' ? 'created as Draft' : 'updated successfully'}`, 'success');
          setFormModalOpen(false);
        } catch (err: any) {
          const message = err?.response?.data?.message || `Failed to ${formMode === 'create' ? 'create' : 'update'} request`;
          showMessage(message, 'error');
        }
      },
    });
  }, [formMode, formData, selectedRecord, showMessage, createRequest, updateRequest, refetch, activeGoldRateId]);

  const handleDirectSubmit = useCallback(async () => {
    // Same validation as save
    if (!formData.loanType) { showMessage('Loan Type is required', 'error'); return; }
    if (!formData.loanAccountNumber.trim()) { showMessage('Loan Account Number is required', 'error'); return; }
    if (!formData.addonCustomerId) { showMessage('Customer ID is required', 'error'); return; }

    // Duplicate loan account number validation (checks both addon requests and loan mappings via API)
    try {
      const isDuplicate = await addonRequestService.checkDuplicateLoanAccount(
        formData.loanAccountNumber.trim()
      );
      if (isDuplicate) {
        showMessage(`Loan Account Number "${formData.loanAccountNumber}" already exists`, 'error');
        return;
      }
    } catch {
      showMessage('Failed to validate loan account number', 'error');
      return;
    }

    const hasEmptyGL = formData.glEntries.some((gl) =>
      !gl.glCustomerId || !gl.glNumber.trim() || !gl.netWeight || !gl.originalLoanAmount || !gl.loanDate
    );
    if (hasEmptyGL) { showMessage('All Gold Loan fields are required', 'error'); return; }

    const glNumbers = formData.glEntries.map((gl) => gl.glNumber.trim().toLowerCase());
    const duplicateGlNumbers = glNumbers.filter((n, i) => glNumbers.indexOf(n) !== i);
    if (duplicateGlNumbers.length > 0) {
      showMessage(`Duplicate GL numbers found: ${[...new Set(duplicateGlNumbers)].join(', ')}. Each GL number must be unique.`, 'error');
      return;
    }

    if (!formData.requestedAmount || parseFloat(formData.requestedAmount) <= 0) {
      showMessage('Amount Required must be greater than 0', 'error'); return;
    }

    setConfirmDialog({
      open: true,
      title: 'Submit Add-On Request',
      message: 'Are you sure you want to submit this add-on request directly for approval?',
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          const created = await createRequest({
              addonCustomerId: formData.addonCustomerId,
              addonLoanType: formData.loanType,
              addonLoanAccountNumber: formData.loanAccountNumber,
              requestedAmount: parseFloat(formData.requestedAmount) || 0,
              goldRateId: activeGoldRateId,
              glEntries: formData.glEntries.map((gl) => ({
                customerId: gl.glCustomerId,
                glNumber: gl.glNumber, netWeight: parseFloat(gl.netWeight) || 0,
                originalLoanAmount: parseFloat(gl.originalLoanAmount) || 0,
                outstandingAmount: gl.outstandingAmount ? parseFloat(gl.outstandingAmount) : null,
                documentationCharge: parseFloat(gl.documentationCharge) || 0,
                loanDate: gl.loanDate,
              })),
            });
            if (created?.id) await submitRequest(created.id);
            await refetch();

          showMessage('Add-on request submitted for approval', 'success');
          setFormModalOpen(false);
        } catch (err: any) {
          const message = err?.response?.data?.message || 'Failed to submit request';
          showMessage(message, 'error');
        }
      },
    });
  }, [formData, showMessage, createRequest, submitRequest, refetch, activeGoldRateId]);

  const handleEditAndSubmit = useCallback(async () => {
    if (!formData.loanType) { showMessage('Loan Type is required', 'error'); return; }
    if (!formData.loanAccountNumber.trim()) { showMessage('Loan Account Number is required', 'error'); return; }
    if (!formData.addonCustomerId) { showMessage('Customer ID is required', 'error'); return; }

    try {
      const isDuplicate = await addonRequestService.checkDuplicateLoanAccount(
        formData.loanAccountNumber.trim(),
        selectedRecord?.id
      );
      if (isDuplicate) {
        showMessage(`Loan Account Number "${formData.loanAccountNumber}" already exists`, 'error');
        return;
      }
    } catch {
      showMessage('Failed to validate loan account number', 'error');
      return;
    }

    const hasEmptyGL = formData.glEntries.some((gl) =>
      !gl.glCustomerId || !gl.glNumber.trim() || !gl.netWeight || !gl.originalLoanAmount || !gl.loanDate
    );
    if (hasEmptyGL) { showMessage('All Gold Loan fields are required', 'error'); return; }

    const glNumbers = formData.glEntries.map((gl) => gl.glNumber.trim().toLowerCase());
    const duplicateGlNumbers = glNumbers.filter((n, i) => glNumbers.indexOf(n) !== i);
    if (duplicateGlNumbers.length > 0) {
      showMessage(`Duplicate GL numbers found: ${[...new Set(duplicateGlNumbers)].join(', ')}. Each GL number must be unique.`, 'error');
      return;
    }

    if (!formData.requestedAmount || parseFloat(formData.requestedAmount) <= 0) {
      showMessage('Amount Required must be greater than 0', 'error'); return;
    }

    setConfirmDialog({
      open: true,
      title: 'Save & Submit Add-On Request',
      message: 'Are you sure you want to save changes and submit this request for approval?',
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          if (!selectedRecord) return;
          const glPayload = formData.glEntries.map((gl) => ({
            customerId: gl.glCustomerId,
            glNumber: gl.glNumber,
            netWeight: parseFloat(gl.netWeight) || 0,
            originalLoanAmount: parseFloat(gl.originalLoanAmount) || 0,
            outstandingAmount: gl.outstandingAmount ? parseFloat(gl.outstandingAmount) : null,
            documentationCharge: parseFloat(gl.documentationCharge) || 0,
            loanDate: gl.loanDate,
          }));

          await updateRequest(selectedRecord.id, {
            id: selectedRecord.id,
            addonCustomerId: formData.addonCustomerId,
            addonLoanType: formData.loanType,
            addonLoanAccountNumber: formData.loanAccountNumber,
            requestedAmount: parseFloat(formData.requestedAmount) || 0,
            goldRateId: activeGoldRateId,
            glEntries: glPayload,
            rowVersion: selectedRecord.rowVersion,
          });
          await submitRequest(selectedRecord.id);
          await refetch();

          showMessage('Request saved and submitted for approval', 'success');
          setFormModalOpen(false);
        } catch (err: any) {
          const message = err?.response?.data?.message || 'Failed to save and submit request';
          showMessage(message, 'error');
        }
      },
    });
  }, [formData, selectedRecord, showMessage, updateRequest, submitRequest, refetch, activeGoldRateId]);

  const handleDelete = useCallback((record: AddonRequestRecord) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Request',
      message: `Are you sure you want to delete request for loan account ${record.addonLoanAccountNumber}?`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          await deleteRequest(record.id);
            await refetch();

          showMessage('Request deleted successfully', 'success');
        } catch (err: any) {
          const apiMessage = err?.response?.data?.message;
          showMessage(apiMessage || 'Failed to delete request', 'error');
        }
      },
    });
  }, [showMessage, deleteRequest, refetch]);

  const handleSubmit = useCallback((record: AddonRequestRecord) => {
    setConfirmDialog({
      open: true,
      title: 'Submit for Approval',
      message: `Are you sure you want to submit request for loan account ${record.addonLoanAccountNumber} (${formatCurrency(record.requestedAmount)}) for approval?`,
      variant: 'default',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        try {
          await submitRequest(record.id);
            await refetch();

          showMessage('Request submitted for approval', 'success');
        } catch (err: any) {
          const message = err?.response?.data?.message || 'Failed to submit request';
          showMessage(message, 'error');
        }
      },
    });
  }, [showMessage, submitRequest, refetch]);

  // ============================================================================
  // ROW ACTIONS
  // ============================================================================

  const getRowActions = useCallback((row: AddonRequestRecord): ActionMenuItem[] => {
    const actions: ActionMenuItem[] = [
      {
        id: 'view',
        label: 'View Details',
        icon: <IconSystem.eye size={18} />,
        onClick: () => handleView(row),
      },
    ];

    if (row.statusCode === 'DRAFT' || row.statusCode === 'RETURNED') {
      actions.push({
        id: 'edit',
        label: 'Edit',
        icon: <IconSystem.edit size={18} />,
        onClick: () => handleEdit(row),
      });
    }

    if (row.statusCode === 'DRAFT' || row.statusCode === 'RETURNED') {
      actions.push({
        id: 'submit',
        label: row.statusCode === 'RETURNED' ? 'Resubmit for Approval' : 'Submit for Approval',
        icon: <IconSystem.check size={18} />,
        onClick: () => handleSubmit(row),
      });
    }

    if (row.statusCode === 'DRAFT') {
      actions.push({
        id: 'delete',
        label: 'Delete',
        icon: <IconSystem.trash size={18} />,
        onClick: () => handleDelete(row),
        variant: 'danger' as const,
      });
    }

    return actions;
  }, [handleView, handleEdit, handleSubmit, handleDelete]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<AddonRequestRecord>[] = useMemo(() => [
    { id: 'addonLoanAccountNumber', header: 'Loan Account #', accessorKey: 'addonLoanAccountNumber', size: 160 },
    { id: 'customerName', header: 'Customer', accessorKey: 'customerName', size: 150 },
    { id: 'businessUnitName', header: 'Branch', accessorKey: 'businessUnitName', size: 140, cell: ({ getValue }: any) => getValue() || '-' },
    {
      id: 'addonLoanType',
      header: 'Loan Type',
      accessorKey: 'addonLoanType',
      size: 140,
      cell: ({ getValue }: any) => {
        const key = getValue();
        return <span>{loanTypeLookup[key] || key}</span>;
      },
    },
    {
      id: 'requestedAmount',
      header: 'Amount Requested',
      accessorKey: 'requestedAmount',
      size: 160,
      cell: ({ getValue }: any) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(getValue())}</span>
      ),
    },
    {
      id: 'statusCode',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 150,
      cell: ({ row }: any) => {
        const code = row.original.statusCode;
        const name = row.original.statusName;
        return (
          <span style={getStatusBadgeStyle(code)}>
            {name || (code ?? '').replace(/_/g, ' ')}
          </span>
        );
      },
    },
    {
      id: 'createdAt',
      header: 'Created At',
      accessorKey: 'createdAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], [loanTypeLookup]);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'New Request',
      onClick: handleAddNew,
      tooltip: 'Create New Add-On Request',
      variant: 'icon',
      visible: true,
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    },
    {
      id: 'export',
      icon: <IconSystem.download size={18} />,
      label: 'Export',
      onClick: () => showMessage('Export functionality will be available after API integration', 'info'),
      tooltip: 'Export',
      variant: 'icon',
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Add-On Requests"
        icon={<IconSystem.add size={20} />}
        description="Create and manage add-on loan requests against gold loan collateral."
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search requests...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      <div className="module-content">
        {apiError && requests.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load requests</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && requests.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <SyutGrid
              data={filteredData}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              enableColumnFilters={false}
              rowActions={getRowActions}
              onSelectionChange={setSelectedRows}
              emptyMessage="No add-on requests found"
            />
          </div>
        </div>
        )}

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'RETURNED', label: 'Returned' },
                  { value: 'REJECTED', label: 'Rejected' },
                ]}
                value={statusFilter}
                onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                placeholder="Search status..."
                isSearchable={true}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Date From</label>
              <input
                type="date"
                className="form-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Date To</label>
              <input
                type="date"
                className="form-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { setStatusFilter(''); setDateFrom(''); setDateTo(''); }}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      <AddonRequestFormModal
        isOpen={formModalOpen}
        mode={formMode}
        formData={formData}
        onFormDataChange={setFormData}
        onSave={handleSaveForm}
        onSubmit={formMode === 'create' ? handleDirectSubmit : handleEditAndSubmit}
        onClose={() => setFormModalOpen(false)}
      />

      {/* View Modal */}
      <AddonRequestViewModal
        isOpen={viewModalOpen}
        record={selectedRecord}
        onClose={() => setViewModalOpen(false)}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        confirmLabel={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
      />
    </div>
  );
};

export default AddonRequestScreen;
